// Plot geometry shared by the threshold sliders (ACT2-534).
//
// Every shape on these charts is positioned by VALUE through the slider's own `pct()`, never by index.
// Index-positioned bars (a `flex-1` row) only coincide with the value-positioned handle, axis track and
// highlight while the bins are exactly equal-width — an invariant nothing on the wire enforces, so a
// histogram that broke it slid the highlight off the bar it was supposed to cover. One mapping function
// for the shapes AND the overlay makes that drift unrepresentable.

import type { CSSProperties } from 'react';

export const PLOT_HEIGHT = 'h-[4.75rem]';
/** Hairline gap between neighbouring bars, split half onto each side of a shared edge. */
export const BAR_GAP = '0.125rem';

/** Absolute box for one value-positioned bar. `max(0rem, …)` guards a razor-thin band's negative width. */
export function valueBarBox(fromPct: number, toPct: number, height: string): CSSProperties {
  return {
    left: `calc(${fromPct}% + ${BAR_GAP} / 2)`,
    width: `max(0rem, calc(${toPct - fromPct}% - ${BAR_GAP}))`,
    height,
  };
}

/** Shortest a bar may draw, in percent of the plot. The two differ on purpose: a measured bucket holding
 *  a handful of rows must stay legible, while a quantile band only needs a visible sliver in a flat tail. */
export const HISTOGRAM_MIN_BAR_PCT = 4;
export const BAND_MIN_BAR_PCT = 2;

/** One interval of the plot: where it sits in VALUE space, and how many rows (or how much share) it holds. */
export interface MassInterval {
  from: number;
  to: number;
  mass: number;
}

/**
 * Value-positioned boxes whose HEIGHT is density — mass per unit of SCREEN — so a bar's AREA is its
 * share of the population even when the intervals are not equal-width.
 *
 * The denominator is the interval's width IN PERCENT OF THE TRACK, not in value. On a linear axis the
 * two are proportional and this is identical to dividing by `to - from`. On a LOG axis (ACT2-569) they
 * diverge by orders of magnitude, and dividing by value width would draw a decade holding a quarter of
 * the population at a hundredth of its neighbour's height — an "area is share" chart that is not one.
 * Area is measured in pixels, so the denominator has to be measured in pixels too.
 *
 * Both sliders draw their body this way and only disagree on where the mass comes from (measured counts
 * vs. quantile deltas) and on `minPct`. Sharing it is what keeps the area invariant one rule rather than
 * two copies that a later fix can land on separately.
 */
export function densityBarBoxes(intervals: readonly MassInterval[], pct: (v: number) => number, minPct: number) {
  const boxes = intervals.map((i) => {
    const fromPct = pct(i.from);
    const toPct = pct(i.to);
    // A zero-width interval has no density at all — dividing by a fallback 1 would hand back the raw
    // mass and draw a full-height bar with no width. The backend folds those away; this is a floor, not
    // a path. Under a log axis it also catches an interval that collapsed on screen despite a real value
    // range, which value-space arithmetic could not have seen.
    return { fromPct, toPct, density: toPct > fromPct ? i.mass / (toPct - fromPct) : 0 };
  });
  const max = Math.max(Number.EPSILON, ...boxes.map((b) => b.density));
  return boxes.map((b) => valueBarBox(b.fromPct, b.toPct, `${Math.max(minPct, (b.density / max) * 100)}%`));
}
